/**
 * Password show/hide toggle
 *
 * Reveals and re-masks the value of a password input from a trailing icon
 * button, so a value typed on a phone can be checked before submitting.
 *
 * Markup contract (see app/views/users/shared/_password_field.html.erb):
 *   div.relative                              the field wrapper - exactly one
 *                                             input and one toggle per wrapper,
 *                                             because the input is resolved
 *                                             positionally from the button
 *     input[type=password]                    the field itself
 *     button.password-toggle                  type="button" so it never submits
 *                                             the form, carrying aria-label and
 *                                             aria-pressed
 *       [data-password-toggle-icon="masked"]    wraps the fa-eye-slash glyph
 *       [data-password-toggle-icon="revealed"]  wraps the fa-eye glyph, `hidden`
 *
 * Both glyphs ship in the markup and the toggle switches which one is `hidden`.
 * Swapping `fa-*` classes on the icon does NOT work: FontAwesome's JS build
 * rewrites each `<i>` into a nested `<svg>` and strips the `fa-*` classes off
 * the `<i>` in the process, so nothing on screen would change. The wrappers are
 * inert markup FontAwesome never touches.
 *
 * The button keeps one constant accessible name and communicates state through
 * `aria-pressed`, the standard toggle-button pattern - the name is not rewritten
 * on activation.
 */

/** The class hook the ERB partial stamps on every toggle button. */
const TOGGLE_SELECTOR = "button.password-toggle";

/** The field wrapper the input is resolved from. */
const WRAPPER_SELECTOR = ".relative";

/** The glyph wrapper shown while the value is masked. */
const MASKED_ICON_SELECTOR = '[data-password-toggle-icon="masked"]';

/** The glyph wrapper shown while the value is revealed. */
const REVEALED_ICON_SELECTOR = '[data-password-toggle-icon="revealed"]';

export interface PasswordToggleHandle {
  /** Tear down the listener this toggle registered. */
  destroy: () => void;
}

/**
 * Mount a single toggle button.
 *
 * @param button - the `.password-toggle` button
 * @returns a handle, or `null` when the button has no input to act on
 */
export function mountPasswordToggle(
  button: HTMLButtonElement,
): PasswordToggleHandle | null {
  const wrapper = button.closest(WRAPPER_SELECTOR);
  const input = wrapper?.querySelector<HTMLInputElement>("input");
  if (!input) return null;

  const maskedIcon = button.querySelector<HTMLElement>(MASKED_ICON_SELECTOR);
  const revealedIcon = button.querySelector<HTMLElement>(
    REVEALED_ICON_SELECTOR,
  );

  /** Reveal or re-mask the field and mirror the new state onto the button. */
  const onClick = (): void => {
    const revealed = input.getAttribute("type") !== "password";
    input.setAttribute("type", revealed ? "password" : "text");
    button.setAttribute("aria-pressed", revealed ? "false" : "true");
    maskedIcon?.toggleAttribute("hidden", !revealed);
    revealedIcon?.toggleAttribute("hidden", revealed);
  };

  button.addEventListener("click", onClick);

  return {
    destroy: () => button.removeEventListener("click", onClick),
  };
}

/**
 * Mount every password toggle found inside a scope.
 *
 * Each field binds independently, so two password fields on one page (the
 * new-password form's password and confirmation) reveal separately.
 *
 * @param scope - the element or document to search; defaults to `document`
 * @returns one handle per mounted toggle - an empty array on a page with no
 *          password field, which is the majority of the auth pages
 */
export function mountPasswordToggles(
  scope: ParentNode = document,
): PasswordToggleHandle[] {
  const buttons = Array.from(
    scope.querySelectorAll<HTMLButtonElement>(TOGGLE_SELECTOR),
  );

  return buttons
    .map((button) => mountPasswordToggle(button))
    .filter((handle): handle is PasswordToggleHandle => handle !== null);
}
